"use client";

import { useCallback, useEffect, useState } from "react";
import { setPageTitle } from "@/lib/page-titles";
import { Eye, EyeOff, X } from "lucide-react";
import { SiteNavigation } from "@/components/ui/site-navigation";
import { toast } from "sonner";
import { useRouter, useSearchParams } from "next/navigation";
import { useSessionContext } from "@supabase/auth-helpers-react";
// Tabs removed — using plain buttons to match reference design
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PillButton } from "@/components/ui/pill-button";
import Link from "next/link";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { isLoginEnabled } from "@/lib/utils";
import { env } from "@/env";
import { Turnstile } from "@/components/ui/turnstile";
import { Mail } from "lucide-react";
import {
  trackCustomEvent,
  trackFormStart,
  trackFormSubmission,
  trackUserLogin,
  trackUserRegistration,
} from "@/lib/analytics";

// Forgot Password Dialog
function ForgotPasswordDialog({
  open,
  onClose,
  supabase,
  initialEmail = "",
}: {
  open: boolean;
  onClose: () => void;
  supabase: any;
  initialEmail?: string;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileSiteKey = env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setEmailSent(false);
      setEmail(initialEmail);
      setCaptchaToken(null);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open, initialEmail]);

  // ESC key to close, Enter to trigger primary action
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        onClose();
      }
      if (e.key === "Enter" && open && emailSent) {
        window.open(
          "https://mail.google.com/mail/u/0/#search/from%3Ahello%40symbolica.ai",
          "_blank"
        );
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, emailSent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    if (turnstileSiteKey && !captchaToken) {
      toast.error("Please complete the verification challenge.");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
        ...(captchaToken ? { captchaToken } : {}),
      });

      if (error) {
        setCaptchaToken(null);
        toast.error(error.message || "Failed to send reset email");
        return;
      }

      setEmailSent(true);
    } catch (error) {
      setCaptchaToken(null);
      toast.error("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  if (!shouldRender) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm transition-opacity duration-200 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 transition-opacity duration-200 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      >
        <div
          className={`w-full max-w-md border border-white/10 bg-[#0a0a0a] p-8 shadow-2xl transition-all duration-200 ${
            isVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {emailSent ? (
            <>
              {/* Email Sent State */}
              <div className="mb-6 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#7678e3]/10">
                  <Mail className="h-6 w-6 text-[#7678e3]" />
                </div>
                <h2 className="font-arizona-sans mb-2 text-xl font-medium text-white">
                  Password reset email sent
                </h2>
                <p className="font-arizona-sans text-sm text-white/60 text-pretty">
                  We've sent a password reset link to{" "}
                  <span className="text-white font-medium">{email}</span>
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <a
                  href="https://mail.google.com/mail/u/0/#search/from%3Ahello%40symbolica.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <PillButton
                    variant="secondary"
                    icon="none"
                    className="w-full"
                  >
                    <svg
                      className="h-4 w-4 mr-2"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" />
                    </svg>
                    Open Gmail
                  </PillButton>
                </a>
                <PillButton
                  variant="secondary"
                  icon="none"
                  onClick={onClose}
                  className="w-full"
                >
                  Close
                </PillButton>
              </div>
            </>
          ) : (
            <>
              {/* Request Email State */}
              <div className="mb-6 text-center">
                <h2 className="font-arizona-sans mb-2 text-xl font-medium text-white">
                  Reset your password
                </h2>
                <p className="font-arizona-sans text-sm text-white/60">
                  Enter your email and we'll send you a reset link.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="forgot-email"
                    className="font-arizona-sans text-[13px] tracking-wide text-white"
                  >
                    Email
                  </Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="font-arizona-sans border-[#2a2a2a] bg-[#1b1b1b] text-white placeholder:text-white/40 focus-visible:border-periwinkle/60 focus-visible:ring-1 focus-visible:ring-periwinkle/30"
                    placeholder="Enter your email"
                    required
                  />
                </div>

                {turnstileSiteKey && (
                  <Turnstile
                    key={captchaToken ? "forgot-has-token" : "forgot"}
                    siteKey={turnstileSiteKey}
                    theme="dark"
                    appearance="interaction-only"
                    onVerify={setCaptchaToken}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => setCaptchaToken(null)}
                  />
                )}

                <div className="flex flex-col gap-3 pt-2">
                  <PillButton
                    variant="secondary"
                    icon="none"
                    type="submit"
                    disabled={
                      isLoading || (!!turnstileSiteKey && !captchaToken)
                    }
                    className="w-full"
                  >
                    {isLoading ? "Sending..." : "Send Reset Link"}
                  </PillButton>
                  <PillButton
                    variant="secondary"
                    icon="none"
                    onClick={onClose}
                    type="button"
                    className="w-full"
                  >
                    Cancel
                  </PillButton>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
}

const signUpSchema = z
  .object({
    name: z.string().min(2, "Name must be at least 2 characters"),
    email: z.string().email("Invalid email address"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type SignUpFormData = z.infer<typeof signUpSchema>;

export default function LoginPage() {
  const searchParams = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"login" | "signup">(
    tabParam === "signup" ? "signup" : "login"
  );
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [hasTrackedSignupStart, setHasTrackedSignupStart] = useState(false);
  const [pageCaptchaToken, setPageCaptchaToken] = useState<string | null>(null);
  const [gatePassed, setGatePassed] = useState(false);
  const turnstileSiteKey = env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const router = useRouter();
  const {
    session,
    isLoading: isSessionLoading,
    supabaseClient: supabase,
  } = useSessionContext();

  // Get the redirect URL from query params, default to /quickstart
  const redirect = searchParams?.get("redirect") || "/quickstart";

  const navigateAfterLogin = useCallback(() => {
    if (redirect.startsWith("http")) {
      window.location.href = redirect;
    } else {
      router.push(redirect);
    }
  }, [redirect, router]);

  const isAgenticaRedirect = redirect.startsWith(
    env.NEXT_PUBLIC_AGENTICA_BASE_URL
  );

  const safeTrack = useCallback((trackFn: () => void) => {
    try {
      trackFn();
    } catch {
      // Analytics is best-effort only.
    }
  }, []);

  const {
    register: registerSignUp,
    handleSubmit: handleSubmitSignUp,
    formState: { errors: signUpErrors },
    reset: resetSignUpForm,
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const loginEnabled = isLoginEnabled();

  // Set page title
  useEffect(() => {
    setPageTitle("Login");
  }, []);

  // Redirect to /agentica if login is disabled
  useEffect(() => {
    if (!loginEnabled) {
      router.replace("/agentica");
    }
  }, [loginEnabled, router]);

  useEffect(() => {
    // Skip redirect while session state is still loading
    if (isSessionLoading) return;

    // Only redirect authenticated users, not anonymous sessions
    if (session?.user && !session.user.is_anonymous) {
      navigateAfterLogin();
    }
  }, [session, isSessionLoading, navigateAfterLogin]);

  useEffect(() => {
    if (activeTab !== "signup" || hasTrackedSignupStart) return;
    safeTrack(() => {
      trackFormStart("signup");
      trackCustomEvent(
        "sign_up_started",
        "authentication",
        "signup_tab_opened",
        {
          source: "login_page_tabs",
        }
      );
    });
    setHasTrackedSignupStart(true);
  }, [activeTab, hasTrackedSignupStart, safeTrack]);

  // Redirect to agentica page when login is disabled
  if (!loginEnabled) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/60" />
      </div>
    );
  }

  // Show loading spinner while checking session to prevent flash
  if (isSessionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/60" />
      </div>
    );
  }

  // If user is authenticated, show loading while redirecting
  if (session?.user && !session.user.is_anonymous) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/60" />
      </div>
    );
  }

  // Page-level Turnstile gate: withhold the login UI until the challenge
  // passes, so bots never see the form.
  if (turnstileSiteKey && !gatePassed) {
    return (
      <div className="relative min-h-screen w-full bg-[#09090a] text-white font-arizona-sans antialiased flex items-center justify-center px-6">
        <div className="w-full max-w-sm flex flex-col items-center gap-8 text-center">
          <h1 className="text-2xl font-normal tracking-[-0.03em]">
            Verifying your browser
          </h1>
          <p className="text-sm font-light text-white/40">
            This takes a moment. You won't need to do anything unless prompted.
          </p>
          <Turnstile
            siteKey={turnstileSiteKey}
            theme="dark"
            onVerify={(token) => {
              setPageCaptchaToken(token);
              setGatePassed(true);
            }}
          />
        </div>
      </div>
    );
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (turnstileSiteKey && !pageCaptchaToken) {
      setLoginError("Please complete the verification challenge.");
      return;
    }
    setIsLoading(true);
    setLoginError(null);
    safeTrack(() =>
      trackCustomEvent(
        "sign_in_submit_attempt",
        "authentication",
        "login_submit_clicked"
      )
    );

    try {
      const x = await supabase.auth.signInWithPassword({
        email,
        password,
        options: pageCaptchaToken
          ? { captchaToken: pageCaptchaToken }
          : undefined,
      });
      const { data, error } = x;

      if (error) {
        safeTrack(() => trackFormSubmission("login", false));
        console.error("Sign in error:", error);
        setPageCaptchaToken(null);
        if (error.message === "Invalid login credentials") {
          setLoginError(
            'Invalid email or password. Don\'t have an account? Click "Sign up" above.'
          );
        } else {
          setLoginError(error.message || "Failed to sign in");
        }
        return;
      }

      if (data.session) {
        safeTrack(() => {
          trackFormSubmission("login", true);
          trackUserLogin("email", email);
        });
        toast.success("Successfully signed in!");
        navigateAfterLogin();
      }
    } catch (error) {
      safeTrack(() => trackFormSubmission("login", false));
      console.error("Sign in exception:", error);
      setPageCaptchaToken(null);
      setLoginError("An unexpected error occurred during sign in");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUpSubmit = async (data: SignUpFormData) => {
    // Validate terms agreement
    if (!agreedToTerms) {
      safeTrack(() =>
        trackCustomEvent(
          "sign_up_terms_missing",
          "authentication",
          "signup_terms_not_accepted"
        )
      );
      toast.error("Please agree to the Terms of Use to continue.");
      return;
    }

    if (turnstileSiteKey && !pageCaptchaToken) {
      toast.error("Please complete the verification challenge.");
      return;
    }

    setSignUpLoading(true);

    try {
      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            name: data.name,
            agreed_to_terms: agreedToTerms,
          },
          emailRedirectTo: redirect.startsWith("http")
            ? `${window.location.origin}/login?redirect=${encodeURIComponent(redirect)}`
            : `${window.location.origin}${redirect}`,
          ...(pageCaptchaToken ? { captchaToken: pageCaptchaToken } : {}),
        },
      });

      if (error) {
        safeTrack(() => trackFormSubmission("signup", false));
        setPageCaptchaToken(null);
        toast.error(error.message || "Failed to sign up");
        return;
      }

      if (authData.user) {
        if (authData.user.identities && authData.user.identities.length === 0) {
          safeTrack(() => trackFormSubmission("signup", false));
          setPageCaptchaToken(null);
          toast.error(
            "This email is already registered. Please sign in instead."
          );
        } else if (!authData.session) {
          safeTrack(() => {
            trackFormSubmission("signup", true);
            trackUserRegistration("email", data.email);
          });
          // Show confirmation box instead of toast
          setSignUpSuccess(data.email);
          resetSignUpForm();
        } else {
          safeTrack(() => {
            trackFormSubmission("signup", true);
            trackUserRegistration("email", data.email);
            trackUserLogin("email", data.email);
          });
          toast.success("Account created successfully!");
          navigateAfterLogin();
        }
      }
    } catch (error) {
      safeTrack(() => trackFormSubmission("signup", false));
      console.error("Sign up exception:", error);
      setPageCaptchaToken(null);
      toast.error("An unexpected error occurred during sign up");
    } finally {
      setSignUpLoading(false);
    }
  };

  const handleSocialLogin = async (provider: "google" | "github") => {
    safeTrack(() => {
      const eventName =
        activeTab === "signup"
          ? "oauth_sign_up_started"
          : "oauth_sign_in_started";
      trackCustomEvent(
        eventName,
        "authentication",
        `${provider}_${activeTab}_start`,
        {
          provider,
          flow: activeTab,
        }
      );
    });
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: redirect.startsWith("http")
          ? `${window.location.origin}/login?redirect=${encodeURIComponent(redirect)}`
          : `${window.location.origin}${redirect}`,
      },
    });
    if (error) {
      safeTrack(() => {
        const eventName =
          activeTab === "signup"
            ? "oauth_sign_up_failed"
            : "oauth_sign_in_failed";
        trackCustomEvent(
          eventName,
          "authentication",
          `${provider}_${activeTab}_error`,
          {
            provider,
            flow: activeTab,
            message: error.message,
          }
        );
      });
      toast.error(error.message);
    }
  };

  return (
    <div className="relative min-h-screen w-full bg-[#09090a] text-white font-arizona-sans antialiased overflow-hidden">
      {/* Top bar */}
      <SiteNavigation
        isDark
        logoOnly
        logo={isAgenticaRedirect ? "agentica" : "symbolica"}
      />

      {/* Split layout */}
      <div className="relative z-10 min-h-screen flex">
        {/* Left side — form with low-opacity blurred backdrop */}
        <div className="w-full lg:w-1/2 relative flex items-center justify-center px-6 sm:px-10">
          <div className="absolute inset-0 backdrop-blur-xl bg-[#09090a]/15 pointer-events-none" />

          {!isAgenticaRedirect && (
            <a
              href={env.NEXT_PUBLIC_AGENTICA_BASE_URL}
              className="absolute top-5 right-6 sm:right-10 z-20 inline-flex items-center gap-2 text-[13px] font-mono font-medium uppercase text-white border border-white/[0.08] rounded-full px-5 py-2 hover:border-white/20 transition-colors duration-200"
            >
              Try Agentica
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </a>
          )}

          <div className="relative z-10 w-full max-w-sm py-32">
            {/* Tab toggle */}
            <div className="flex items-center gap-1 p-1 rounded-full border border-white/[0.08] bg-white/[0.03] mb-10 w-fit">
              <button
                type="button"
                onClick={() => setActiveTab("login")}
                className={`text-[13px] font-mono font-medium uppercase px-5 py-2 rounded-full transition-all duration-200 cursor-pointer ${
                  activeTab === "login"
                    ? "bg-white text-[#09090a]"
                    : "text-white hover:text-white/70"
                }`}
              >
                Log in
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveTab("signup");
                  safeTrack(() =>
                    trackCustomEvent(
                      "sign_up_tab_clicked",
                      "authentication",
                      "signup_tab_clicked"
                    )
                  );
                }}
                className={`text-[13px] font-mono font-medium uppercase px-5 py-2 rounded-full transition-all duration-200 cursor-pointer ${
                  activeTab === "signup"
                    ? "bg-white text-[#09090a]"
                    : "text-white hover:text-white/70"
                }`}
              >
                Sign up
              </button>
            </div>

            {/* Heading */}
            <h1 className="text-3xl sm:text-4xl font-normal tracking-[-0.03em] leading-[1.1] mb-3">
              {activeTab === "login"
                ? isAgenticaRedirect
                  ? "Long-running agents at your fingertips."
                  : "The Platform for the Agentica SDK."
                : "Create your account."}
            </h1>
            <p className="text-sm font-light text-white/40 mb-10">
              {activeTab === "login"
                ? isAgenticaRedirect
                  ? "Sign in to Agentica."
                  : "Sign in to the Platform."
                : "Get started with Agentica today."}
            </p>

            {/* Login form */}
            {activeTab === "login" && (
              <>
                {/* Social sign-in */}
                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={() => handleSocialLogin("google")}
                    className="w-full inline-flex items-center justify-center gap-3 text-sm font-mono font-medium uppercase text-white border border-white/[0.08] rounded-full px-6 py-3 hover:border-white/20 transition-colors duration-200 cursor-pointer"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </button>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-4 my-8">
                  <div className="flex-1 border-t border-white/[0.06]" />
                  <span className="text-xs font-ibm-plex-mono text-white/20 uppercase">
                    or
                  </span>
                  <div className="flex-1 border-t border-white/[0.06]" />
                </div>

                <form onSubmit={handleSignIn} className="flex flex-col gap-5">
                  <div>
                    <label className="block text-xs font-ibm-plex-mono text-white/30 uppercase tracking-widest mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setLoginError(null);
                      }}
                      className="w-full bg-transparent border border-white/[0.08] rounded-lg px-4 py-3 text-sm font-light text-white placeholder-white/20 outline-none focus:border-white/20 transition-colors duration-200"
                      placeholder="you@example.com"
                      required
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-ibm-plex-mono text-white/30 uppercase tracking-widest">
                        Password
                      </label>
                      <button
                        type="button"
                        className="font-ibm-plex-mono hover:text-white cursor-pointer text-[11px] text-white/30 uppercase tracking-widest transition-colors"
                        onClick={() => setShowForgotPassword(true)}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setLoginError(null);
                        }}
                        className="w-full bg-transparent border border-white/[0.08] rounded-lg px-4 py-3 pr-10 text-sm font-light text-white placeholder-white/20 outline-none focus:border-white/20 transition-colors duration-200"
                        placeholder="••••••••"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 transition-colors hover:text-white/60"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={
                      isLoading || (!!turnstileSiteKey && !pageCaptchaToken)
                    }
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 text-sm font-mono font-medium uppercase text-[#09090a] bg-white rounded-full px-6 py-3 hover:bg-white/90 transition-colors duration-200 cursor-pointer disabled:opacity-50"
                  >
                    {isLoading ? "Loading..." : "Log in"}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </button>
                </form>

                {loginError && (
                  <div className="mt-4 bg-red-500/[0.08] border border-red-500/30 rounded-lg px-4 py-3">
                    <p className="text-sm text-red-400">{loginError}</p>
                  </div>
                )}
              </>
            )}

            {/* Sign up form */}
            {activeTab === "signup" && (
              <>
                {signUpSuccess ? (
                  <div className="space-y-6">
                    <div className="bg-periwinkle/[0.08] backdrop-blur-md border border-periwinkle p-6 rounded-lg">
                      <h3 className="font-arizona-sans text-lg font-medium text-white mb-3">
                        Check your email
                      </h3>
                      <p className="font-arizona-sans text-sm text-white/80 mb-4">
                        We&apos;ve sent a confirmation link to{" "}
                        <span className="text-white font-medium">
                          {signUpSuccess}
                        </span>
                      </p>
                      <p className="font-arizona-sans text-sm text-white/60 mb-6">
                        Click the link in your email to confirm your account and
                        get started.
                      </p>
                      <a
                        href="https://mail.google.com/mail/u/0/#search/from%3Ahello%40symbolica.ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <PillButton
                          variant="secondary"
                          icon="none"
                          className="w-full"
                        >
                          <svg
                            className="h-4 w-4 mr-2"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" />
                          </svg>
                          Open Gmail
                        </PillButton>
                      </a>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Social sign-up */}
                    <div className="flex flex-col gap-3">
                      <button
                        type="button"
                        onClick={() => handleSocialLogin("google")}
                        className="w-full inline-flex items-center justify-center gap-3 text-sm font-mono font-medium uppercase text-white border border-white/[0.08] rounded-full px-6 py-3 hover:border-white/20 transition-colors duration-200 cursor-pointer"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        Continue with Google
                      </button>
                    </div>

                    {/* Divider */}
                    <div className="flex items-center gap-4 my-8">
                      <div className="flex-1 border-t border-white/[0.06]" />
                      <span className="text-xs font-ibm-plex-mono text-white/20 uppercase">
                        or
                      </span>
                      <div className="flex-1 border-t border-white/[0.06]" />
                    </div>

                    <form
                      onSubmit={handleSubmitSignUp(
                        (formData) => {
                          safeTrack(() =>
                            trackCustomEvent(
                              "sign_up_submit_attempt",
                              "authentication",
                              "signup_submit_clicked"
                            )
                          );
                          return handleSignUpSubmit(formData);
                        },
                        () => {
                          safeTrack(() =>
                            trackCustomEvent(
                              "sign_up_submit_attempt",
                              "authentication",
                              "signup_submit_clicked"
                            )
                          );
                        }
                      )}
                      className="flex flex-col gap-5"
                    >
                      <div>
                        <label className="block text-xs font-ibm-plex-mono text-white/30 uppercase tracking-widest mb-2">
                          Name
                        </label>
                        <input
                          type="text"
                          {...registerSignUp("name")}
                          className="w-full bg-transparent border border-white/[0.08] rounded-lg px-4 py-3 text-sm font-light text-white placeholder-white/20 outline-none focus:border-white/20 transition-colors duration-200"
                          placeholder="Your name"
                        />
                        {signUpErrors.name && (
                          <p className="text-xs text-red-400 mt-1">
                            {signUpErrors.name.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-ibm-plex-mono text-white/30 uppercase tracking-widest mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          {...registerSignUp("email")}
                          className="w-full bg-transparent border border-white/[0.08] rounded-lg px-4 py-3 text-sm font-light text-white placeholder-white/20 outline-none focus:border-white/20 transition-colors duration-200"
                          placeholder="you@example.com"
                        />
                        {signUpErrors.email && (
                          <p className="text-xs text-red-400 mt-1">
                            {signUpErrors.email.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-ibm-plex-mono text-white/30 uppercase tracking-widest mb-2">
                          Password
                        </label>
                        <input
                          type="password"
                          {...registerSignUp("password")}
                          className="w-full bg-transparent border border-white/[0.08] rounded-lg px-4 py-3 text-sm font-light text-white placeholder-white/20 outline-none focus:border-white/20 transition-colors duration-200"
                          placeholder="••••••••"
                        />
                        {signUpErrors.password && (
                          <p className="text-xs text-red-400 mt-1">
                            {signUpErrors.password.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-ibm-plex-mono text-white/30 uppercase tracking-widest mb-2">
                          Confirm Password
                        </label>
                        <input
                          type="password"
                          {...registerSignUp("confirmPassword")}
                          className="w-full bg-transparent border border-white/[0.08] rounded-lg px-4 py-3 text-sm font-light text-white placeholder-white/20 outline-none focus:border-white/20 transition-colors duration-200"
                          placeholder="••••••••"
                        />
                        {signUpErrors.confirmPassword && (
                          <p className="text-xs text-red-400 mt-1">
                            {signUpErrors.confirmPassword.message}
                          </p>
                        )}
                      </div>

                      {/* Terms Checkbox */}
                      <div className="pt-1">
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <div className="relative mt-0.5 flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={agreedToTerms}
                              onChange={(e) =>
                                setAgreedToTerms(e.target.checked)
                              }
                              className="peer sr-only"
                            />
                            <div className="h-[18px] w-[18px] rounded border border-white/[0.08] bg-transparent transition-all duration-200 peer-checked:border-periwinkle/60 peer-checked:bg-periwinkle/10 peer-focus:border-periwinkle/40" />
                            <svg
                              className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 text-periwinkle opacity-0 transition-opacity duration-200 peer-checked:opacity-100"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path
                                d="M2.5 6L5 8.5L9.5 3.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <span className="text-[13px] text-white/40 leading-relaxed group-hover:text-white/60 transition-colors">
                            By using Agentica, you agree to our{" "}
                            <Link
                              href="/terms/terms-of-use"
                              className="hover:text-white transition-colors underline"
                              target="_blank"
                            >
                              Terms of Use
                            </Link>{" "}
                            and{" "}
                            <Link
                              href="/terms/privacy-policy"
                              className="hover:text-white transition-colors underline"
                              target="_blank"
                            >
                              Privacy Policy
                            </Link>
                          </span>
                        </label>
                      </div>

                      <button
                        type="submit"
                        disabled={
                          signUpLoading ||
                          (!!turnstileSiteKey && !pageCaptchaToken)
                        }
                        className="mt-2 w-full inline-flex items-center justify-center gap-2 text-sm font-mono font-medium uppercase text-[#09090a] bg-white rounded-full px-6 py-3 hover:bg-white/90 transition-colors duration-200 cursor-pointer disabled:opacity-50"
                      >
                        {signUpLoading ? "Loading..." : "Create Account"}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        </svg>
                      </button>
                    </form>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Divider line */}
        <div className="hidden lg:block w-px bg-white/[0.08]" />

        {/* Right side — particles visible, no content */}
        <div className="hidden lg:block lg:flex-1" />
      </div>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        supabase={supabase}
        initialEmail={email}
      />

      {/*
        Invisible token refresher. Turnstile tokens are single-use, so after a
        submit consumes the current one we remount (key flips on null) to get a
        fresh token without bouncing the user back to the gate screen.
      */}
      {turnstileSiteKey && !pageCaptchaToken && (
        <div className="fixed bottom-4 right-4 z-50">
          <Turnstile
            key="token-refresher"
            siteKey={turnstileSiteKey}
            theme="dark"
            appearance="interaction-only"
            onVerify={setPageCaptchaToken}
          />
        </div>
      )}
    </div>
  );
}
